import { InputGroupDisable } from "@funded-here-interface/common";
import { FC, useMemo } from "react";
import { Data, UserData } from "../../../types/org.type";
import { useStyles } from "./Individual.styles";
import { capitalizeFirstLetter } from "@funded-here-interface/common/src/Utils/string";
import { convertDate } from "@funded-here-interface/common/src/Utils/date";
import { codeToCountryNameMap } from "@funded-here-interface/common/src/constant/country";
import { PERSONA_DISPLAY_MAP } from "@funded-here-interface/common/src/constant/constant";

interface IProps {
  data: Data | undefined;
  userData: UserData | undefined;
}

interface FieldConfig {
  label: string;
  id: string;
  value: string | number;
  shouldShow?: boolean;
}

const Individual: FC<IProps> = ({ data, userData }) => {
  const { classes } = useStyles();

  // Compute onboarded as value
  const rawRoleName = userData?.orgRole?.[0]?.role?.name;
  let onboardedAs = rawRoleName
    ? PERSONA_DISPLAY_MAP[rawRoleName] || capitalizeFirstLetter(rawRoleName)
    : "-";
  if (userData?.investorCategory?.category) {
    onboardedAs = `${capitalizeFirstLetter(
      userData?.investorCategory?.category
    )} ${onboardedAs}`;
  }

  const taxResidenceCountries = data?.taxResidenceCountries || [];

  // Define left column field configurations
  const leftColumnFields = useMemo<FieldConfig[]>(
    () => [
      {
        label: "Email",
        id: "email",
        value: userData?.email ?? "-",
      },
      {
        label: "Phone Number",
        id: "phoneNumber",
        value: data?.companyPhoneNumber ?? "-",
      },
      {
        label: "Onboarded as",
        id: "Onboarded as",
        value: onboardedAs,
      },
      {
        label: "Date onboarded",
        id: "Date onboarded",
        value: data?.createdDate ? convertDate(data.createdDate) : "-",
      },
      {
        label: "First Name",
        id: "first_name",
        value: capitalizeFirstLetter(userData?.firstName) || "-",
      },
      {
        label: "Last Name",
        id: "last_name",
        value: capitalizeFirstLetter(userData?.lastName) || "-",
      },
      {
        label: "Industry Group",
        id: "industry_group",
        value: data?.industryGroup || "-",
      },
      {
        label: "Industry",
        id: "industry",
        value: data?.industry || "-",
      },
      {
        label: "Employment Status",
        id: "employment_status",
        value: data?.employmentStatus || "-",
      },
      {
        label: "Employment Position",
        id: "employment_position",
        value: data?.employmentPosition || "-",
      },
      {
        label: "Specific Occupation",
        id: "occupation",
        value: data?.occupation || "-",
      },
      {
        label: "No. of Years in this Occupation",
        id: "occupation_experience",
        value: data?.numberofYears || "-",
      },
    ],
    [data, userData, onboardedAs]
  );

  // Define right column field configurations
  const rightColumnFields = useMemo<FieldConfig[]>(
    () => [
      {
        label: "Gender",
        id: "gender",
        value: data?.gender || "-",
      },
      {
        label: "Date of Birth",
        id: "date_of_birth",
        value: data?.dateOfBirth ? convertDate(data?.dateOfBirth) : "-",
      },
      {
        label: "Country of Birth",
        id: "country_of_birth",
        value: data?.countryOfBirth || "-",
      },
      {
        label: "Nationality",
        id: "nationality",
        value: data?.nationality || "-",
      },
      {
        label: "Country of residence",
        id: "country_of_residence",
        value: data?.countryOfIncorporation || "-",
      },
      {
        label: "Address",
        id: "address",
        value: data?.legalEntityAddress || "-",
      },
      {
        label: "Postal Code",
        id: "postal_code",
        value: data?.postalCode || "-",
      },
      {
        label: "Investment Mode",
        id: "investment_mode",
        value:
          capitalizeFirstLetter(data?.investmentPreferences?.investmentMode) ||
          "-",
      },
      {
        label: "Planned Investment Amount (SGD)",
        id: "investment_amount",
        value: data?.investmentPreferences?.investmentSize || "-",
      },
      {
        label: "Are you a Politically Exposed Person (PEP)?",
        id: "is_pep",
        value: data?.isPep ? "Yes" : "No",
      },
      {
        label: "Current Employer Name",
        id: "employer_name",
        value: data?.employerName || "-",
      },
      {
        label: "How Did You Hear About Us?",
        id: "referral_source",
        value: data?.referralSource || "-",
      },
      {
        label: "Briefly specify how you found out about us",
        id: "other_referral_source",
        value: data?.otherReferral || "-",
        shouldShow: !!data?.otherReferral,
      },
    ],
    [data]
  );

  // Helper function to render fields
  const renderFields = (fields: FieldConfig[]) =>
    fields
      .filter((field) => field.shouldShow !== false)
      .map((field) => (
        <InputGroupDisable
          key={field.id}
          labelText={field.label}
          id={field.id}
          value={field.value}
        />
      ));

  return (
    <div className={classes.valueContainer}>
      <div>{renderFields(leftColumnFields)}</div>
      <div>{renderFields(rightColumnFields)}</div>
      {/* Declaration of Tax Residency Section */}
      <div className={classes.taxResidencySection}>
        <strong>Declaration of Tax Residency</strong>
        <br />
        <span style={{ display: "block", marginBottom: "24px" }}>
          Reasons if a TIN is unavailable:
          <br /> Reason A: The jurisdiction where the Account Holder is liable
          to pay tax does not issue TINs to its residents.
          <br />
          Reason B: The Account Holder is otherwise unable to obtain a TIN or
          equivalent number.
          <br />
          Reason C: No TIN is required.
        </span>
        <InputGroupDisable
          labelText="Number of Country of Tax Residence"
          id="numberOfTaxResidenceCountries"
          value={data?.numberOfTaxResidenceCountries ?? "-"}
        />

        {taxResidenceCountries.length > 0 &&
          taxResidenceCountries.map((country: any, index: number) => (
            <div
              key={index}
              style={{
                marginTop: "24px",
                paddingTop: "16px",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <InputGroupDisable
                labelText={`${index + 1}. Country of Tax Residence`}
                id={`taxResidenceCountry${index}`}
                value={country.country || "-"}
              />

              <InputGroupDisable
                labelText={`${index + 1}. Taxpayer Identification Number (TIN)`}
                id={`taxResidenceTin${index}`}
                value={country.tin || "-"}
              />

              {country.tin === "-" && country.reason && (
                <InputGroupDisable
                  labelText={`${index + 1}. Reason (if TIN is unavailable)`}
                  id={`taxResidenceReason${index}`}
                  value={country.reason}
                />
              )}

              {country.reason === "Reason B" && country.explanation && (
                <InputGroupDisable
                  labelText={`${index + 1}. Explanation for Reason B`}
                  id={`taxResidenceExplanation${index}`}
                  value={country.explanation}
                />
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default Individual;
